import { useCallback, useEffect, useState, useRef } from "react";
import useAxiosClient from "../axios-client";

/**
 * The fabric colour list, paged and searchable.
 *
 * `includeStoreOnly` asks for the colours that name a shelf rather than a
 * product — "Mixed", which the Fabric Store counts Printed under. Off by
 * default so the order form, which must never offer it, gets the safe list
 * without opting out of anything.
 */
export const useFabrics = ({ includeStoreOnly = false } = {}) => {

    const axiosClient = useAxiosClient();
    const [fabrics, setFabrics] = useState([]);
    const [fabricsLoading, setFabricsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);

    const searchQueryRef = useRef('');

    const fetchFabrics = useCallback(async (pageNumber = 1, search = null) => {
        setFabricsLoading(true);

        if (pageNumber === 1 && typeof search === 'string') {
            searchQueryRef.current = search;
        }

        const currentSearch = (pageNumber === 1 && typeof search === 'string') ? search : searchQueryRef.current;

        try {
            // Built rather than interpolated: a search term is user input and
            // may carry an & or a #, which spliced straight into the query
            // string would silently truncate it.
            const params = new URLSearchParams({ page: pageNumber });
            if (currentSearch) params.set('search', currentSearch);
            if (includeStoreOnly) params.set('includeStoreOnly', '1');

            const response = await axiosClient.get(`/settings/fabrics/index?${params.toString()}`);
            const newFabrics = response.data.data || [];
            const total = response.data.total ?? 0;

            if (pageNumber === 1) {
                setFabrics(newFabrics);
                setPage(1);
                setTotalCount(total);
            } else {
                setFabrics(prev => [...prev, ...(newFabrics || [])]);
                setPage(pageNumber);
                if (total) setTotalCount(total);
            }

            setHasMore(!!response.data.next_page_url);
        } catch (error) {
            // Silently handle fetch errors
        } finally {
            setFabricsLoading(false);
        }
    }, [axiosClient, includeStoreOnly]);

    const loadMore = useCallback(() => {
        if (!fabricsLoading && hasMore) {
            fetchFabrics(page + 1);
        }
    }, [fabricsLoading, hasMore, page, fetchFabrics]);

    useEffect(() => {
        fetchFabrics(1);
    }, [fetchFabrics]);

    return { fabrics, fabricsLoading, fetchFabrics, loadMore, hasMore, totalCount, setFabrics };
}
